import dayjs from "dayjs";
import React, { useEffect } from "react";
import Config from "../util/config";
import Calendar from "./calendar/Calendar";
import MonthSelect from "./MonthSelect";
import Loader from "./Loader";

interface Props { }

interface State {
  start: string; // start month
  loading: boolean;
  error: boolean;
  errorMessage: string;
  selected: string; // start date
  endSelected: string; // end date
  calendarsToDisplay: number;
  selectableDates: string[];
  nights: number;
  calendarElemToSetText: any;
}

class App extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.onMonthChange = this.onMonthChange.bind(this);
    this.onDaySelect = this.onDaySelect.bind(this);

    this.state = {
      start: Config.get("start", dayjs("YYYY-MM")),
      selected: Config.get("selected", ""),
      endSelected: Config.get("endSelected", ""),
      loading: false,
      error: false,
      errorMessage: "",
      calendarsToDisplay: Config.get("calendarsToDisplay", 1),
      selectableDates: [],
      nights: Config.get("nights", 0),
      calendarElemToSetText: document.getElementById(Config.get("calendarId")),
    };

    this.updateCalendarButtonText();
  }

  componentWillUpdate(p: any, c: any): void {
    this.updateCalendarButtonText(c);
  }

  onMonthChange(start: string): void {
    this.setState({
      start,
    });
  }

  onDaySelect(day: dayjs.Dayjs): void {
    this.selectRange(day);
  }

  updateCalendarButtonText(stateToUse: any = this.state): void {
    const calendar = document.getElementById(Config.get("calendarId"));
    if (calendar != undefined || calendar != null) {
      calendar.innerText = this.generateDatesText(stateToUse);
    }
  }

  generateDatesText(stateToUse: any = this.state): string {
    let dateString = Config.get("buttonText", "Start Date");
    if (stateToUse.selected !== "") {
      dateString = dayjs(stateToUse.selected).format("DD/MM/YYYY");
    }
    if (stateToUse.endSelected !== "") {
      dateString += " to " + dayjs(stateToUse.endSelected).format("DD/MM/YYYY");
    }
    return dateString;
  }

  prepareDate(date: dayjs.Dayjs): string {
    return date.format("YYYY-MM-DD");
  }

  selectRange(selectedDate: dayjs.Dayjs): void {
    const oldDate = this.state.selected;
    const hasSelectedDay = oldDate.length > 0;
    let selected = this.prepareDate(selectedDate);

    if (
      hasSelectedDay &&
      oldDate != selected &&
      this.state.nights == 0 &&
      this.state.endSelected == ""
    ) {
      // User selects end date
      const startDate = dayjs(oldDate);
      const nights = Math.abs(selectedDate.diff(startDate, "days"));

      // Set earliest date to start date
      selected = startDate.isBefore(selectedDate, "days") ? oldDate : selected;
      let endDate = startDate.isBefore(selectedDate, "days")
        ? selectedDate.format("YYYY-MM-DD")
        : oldDate;
      this.setState({
        selected,
        nights: nights,
        endSelected: endDate,
      });

      const datesSelectedEvent = new CustomEvent("datesSelected");
      window.dispatchEvent(datesSelectedEvent);

      if (this.state.calendarElemToSetText) {
        this.state.calendarElemToSetText.innerText =
          dayjs(selected).format("DD/MM/YYYY") +
          " to " +
          dayjs(endDate).format("DD/MM/YYYY");
      }
    } else {
      this.setState({
        selected,
        nights: 0,
        endSelected: "",
      });
      if (this.state.calendarElemToSetText) {
        this.state.calendarElemToSetText.innerText =
          dayjs(selected).format("DD/MM/YYYY");
      }
    }

    const evt = new Event("gloversure.accommodation.filters.change");
    document.dispatchEvent(evt);
  }

  resetDates = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();

    this.setState({
      selected: "",
      nights: 0,
      endSelected: "",
    });
    if (this.state.calendarElemToSetText) {
      this.state.calendarElemToSetText.innerText = Config.get(
        "buttonText",
        "Start date",
      );
    }
    const evt = new Event("gloversure.accommodation.filters.change");
    document.dispatchEvent(evt);
  };

  getAttributesForElem(elemName: string) {
    const attributes = Config.get("attributes", {});
    return elemName in attributes && !!Object.keys(attributes[elemName]).length
      ? attributes[elemName]
      : {};
  }

  render(): JSX.Element {
    const {
      start,
      selected,
      loading,
      selectableDates,
      calendarsToDisplay,
      nights,
      endSelected,
    } = this.state;

    const containerClasses: string[] = ["calendar-container"];

    return (
      <div className={containerClasses.join(" ")}>
        {loading && <Loader />}
        {Config.get("no-prices", false) && (
          <>
            <div className="no-price-overlay" />
            <div
              className="no-prices-msg"
              dangerouslySetInnerHTML={{ __html: Config.get("no-price-msg") }}
            />
          </>
        )}

        <input
          type="hidden"
          className="start_date"
          name="start_date"
          data-layer-name="Start Date"
          value={selected}
          {...this.getAttributesForElem("start_date")}
        />
        <input
          type="hidden"
          className="nights"
          value={nights}
          name="nights"
          data-layer-name="Nights"
          {...this.getAttributesForElem("nights")}
        />
        <input
          type="hidden"
          className="end_date"
          value={endSelected}
          name="end_date"
          data-layer-name="End Date"
          {...this.getAttributesForElem("end_date")}
        />

        <MonthSelect
          month={start}
          monthsToDisplay={Config.get("monthsToDisplay", 18)}
          onChange={this.onMonthChange}
          calendarsToDisplay={calendarsToDisplay}
        />

        {[...Array.from(Array(calendarsToDisplay))].map((e, i) => (
          <Calendar
            key={"calendar-view-" + i}
            start={dayjs(start).add(i, "months").format("YYYY-MM")}
            selectable={selectableDates}
            selected={selected}
            onDaySelect={this.onDaySelect}
            nights={nights}
            endSelected={endSelected}
          />
        ))}

        {selected && (
          <button
            type="button"
            className="reset-dates"
            onClick={(e) => this.resetDates(e)}
          >
            {Config.get("reset_dates_text", "Reset Dates")}
          </button>
        )}
      </div>
    );
  }
}

export default App;
